import { ReactNode, useMemo, useState } from 'react';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { useShouldEnableLegPricing } from '#/unified-rfqs/ui/hooks/shared';
import { useUMPlatformCreationDisabled } from '#/platform-state/contexts/providers';
import { useUnifiedRfqsService } from '#/unified-rfqs/contexts/unified-rfqs';

export type NullableSide = Side | null;

interface QuoteBuilderModalMethods {
  readonly open: (params: OpenParams) => void;
  readonly close: () => void;
  readonly setLegPricing: (enabled: boolean) => void;
  readonly setSelectedSide: (side: NullableSide) => void;
  readonly setIsMarkPricePrefill: (value: boolean) => void;
}

interface OpenParams {
  readonly rfqId: Drfq['id'];
  readonly side: NullableSide;
}

interface QuoteBuilderModalStates {
  readonly rfqId?: Drfq['id'];
  readonly isOpen: boolean;
  readonly legPricingEnabled: boolean;
  readonly side: NullableSide;
  readonly isMarkPricePrefill: boolean;
}

export const [useModalState, ModalStateProvider] =
  createSafeContext<QuoteBuilderModalStates>('QuoteBuilderModalState');

export const [useModalMethods, ModalMethodsProvider] =
  createSafeContext<QuoteBuilderModalMethods>('QuoteBuilderModalMethods');

interface Props {
  readonly children: ReactNode;
}

export const QuoteBuilderModalProvider = ({ children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [forcedLegPricing, setForcedLegPricing] = useState(false);
  const [isMarkPricePrefill, setIsMarkPricePrefill] = useState(false);
  const [selectedRfqId, setSelectedRfqId] = useState<Drfq['id']>();
  const [selectedSide, setSelectedSide] = useState<NullableSide>(null);
  const { selectById } = useUnifiedRfqsService();
  const rfq = selectedRfqId != null ? selectById(selectedRfqId) : null;

  const methods = useMemo(
    (): QuoteBuilderModalMethods => ({
      open: ({ rfqId, side }) => {
        setIsOpen(true);
        setSelectedRfqId(rfqId);
        setSelectedSide(side);
      },
      close: () => {
        setIsOpen(false);
        setForcedLegPricing(false);
        setSelectedSide(null);
      },
      setLegPricing: (visible) => setForcedLegPricing(visible),
      setIsMarkPricePrefill: (value) => setIsMarkPricePrefill(value),
      setSelectedSide: (side) => setSelectedSide(side),
    }),
    [],
  );

  const legPricingEnabled = useShouldEnableLegPricing(forcedLegPricing, rfq);
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const state: QuoteBuilderModalStates = useMemo(
    () => ({
      isOpen: isPlatformCreationDisabled ? false : isOpen,
      rfqId: selectedRfqId,
      legPricingEnabled,
      side: selectedSide,
      isMarkPricePrefill,
    }),
    [
      isOpen,
      isPlatformCreationDisabled,
      legPricingEnabled,
      selectedRfqId,
      selectedSide,
      isMarkPricePrefill,
    ],
  );
  return (
    <ModalStateProvider value={state}>
      <ModalMethodsProvider value={methods}>{children}</ModalMethodsProvider>
    </ModalStateProvider>
  );
};

export const useQuoteBuilderModalState = useModalState;
export const useQuoteBuilderModalMethods = useModalMethods;
